"use client";
import type { ApiObject } from "@rudderstack/analytics-js";
import { useReportWebVitals } from "next/web-vitals";
import type {
  CLSMetricWithAttribution,
  INPMetricWithAttribution,
  LCPMetricWithAttribution,
  Metric, // base interface
} from "web-vitals";

import { useAnalytics } from "@/contexts/analytics-context";

// Helpful union so TypeScript knows about the extra `attribution` object:
type MetricWithAttribution =
  | CLSMetricWithAttribution
  | INPMetricWithAttribution
  | LCPMetricWithAttribution
  | Metric;

/** Declare the gtag.js global (comes from @types/gtag.js). */
declare global {
  interface Window {
    gtag: Gtag.Gtag;
  }
}

export function WebVitalsAnalyticsHandler() {
  const Analytics = useAnalytics();

  useReportWebVitals((metric: MetricWithAttribution) => {
    const { name, delta, value, id } = metric;

    const attribution =
      "attribution" in metric && typeof metric.attribution === "object" ? metric.attribution : {};

    const params: Gtag.EventParams & {
      metric_id: string;
      metric_delta: number;
      metric_value: number;
      debug_target?: string;
      non_interaction?: boolean;
    } = {
      value: delta,
      metric_id: id,
      metric_value: value,
      metric_delta: delta,
      non_interaction: true,
    };

    // Debug helpers
    switch (name) {
      case "CLS":
        if (
          "largestShiftElement" in attribution &&
          typeof attribution.largestShiftElement === "string"
        ) {
          params.debug_target = attribution.largestShiftElement;
        }
        break;
      case "INP":
        if (
          "interactionTarget" in attribution &&
          typeof attribution.interactionTarget === "string"
        ) {
          params.debug_target = attribution.interactionTarget;
        }
        break;
      case "LCP":
        if ("element" in attribution && typeof attribution.element === "string") {
          params.debug_target = attribution.element;
        }
        break;
    }
    Analytics.trackEvent(name, params as unknown as ApiObject);
  });

  return null;
}
